/**
 * SearchUserItem Component
 * Individual user row in search results
 * Matches Flutter's search_screen.dart userItem
 */

import React from 'react';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { useLocale } from '@/presentation/contexts/LocaleContext';
import { CachedImage } from '@/presentation/components/common/CachedImage';
import { IdentityBadge } from '@/presentation/components/common/identity/IdentityBadge';
import { UserUtils } from '@/domain/utils/userUtils';
import { Navigator } from '@/presentation/routing/Navigator';
import type { UserEntity } from '@/domain/entities/user/userEntity';
import { qaAttr } from '@/presentation/utils/qaAttr';

export interface SearchUserItemProps {
  user: UserEntity;
}

export const SearchUserItem: React.FC<SearchUserItemProps> = ({ user }) => {
  const { theme } = useTheme();
  const responsive = useResponsiveDesign();
  const { l10n } = useLocale();

  const subscribersText = user.subscribers === 1
    ? `1 ${l10n.search.subscriber}`
    : `${user.subscribers} ${l10n.search.subscribers}`;

  const postsText = user.contentCount === 1
    ? `1 ${l10n.search.post}`
    : `${user.contentCount} ${l10n.search.posts}`;

  return (
    <div
      {...qaAttr({ id: `search-result-user-${user.firebaseId}` })}
      onClick={() => Navigator.navigateToUserDetails(user.firebaseId)}
      style={{
        display: 'flex',
        alignItems: 'center',
        cursor: 'pointer',
        paddingBottom: responsive.spacing.lg,
      }}
    >
      <CachedImage
        src={UserUtils.generateUserProfileImageUrl(user.firebaseId)}
        alt={user.name}
        style={{
          width: responsive.avatar.medium,
          height: responsive.avatar.medium,
          borderRadius: responsive.borderRadius.round,
          objectFit: 'cover',
        }}
      />

      <div style={{ flex: 1, marginLeft: responsive.spacing.base }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: responsive.spacing.xs }}>
          <p
            style={{
              fontWeight: responsive.fontWeight.semibold,
              color: theme.text.primary,
              fontSize: responsive.font.base,
              margin: 0,
            }}
          >
            {user.name}
          </p>
          <IdentityBadge userId={user.firebaseId} />
        </div>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: responsive.spacing.xs,
            marginTop: responsive.spacing.xs,
          }}
        >
          <span style={{ fontSize: responsive.font.sm, color: theme.text.secondary }}>
            {subscribersText}
          </span>
          <span
            style={{
              width: responsive.separator.horizontal,
              height: responsive.separator.vertical,
              borderRadius: responsive.borderRadius.round,
              backgroundColor: theme.text.secondary,
              display: 'inline-block',
            }}
          />
          <span style={{ fontSize: responsive.font.sm, color: theme.text.secondary }}>
            {postsText}
          </span>
        </div>
      </div>
    </div>
  );
};
